'use client'
import Image from 'next/image'

import { PromotionsEnquireDrawer } from '@/components/common/PromotionsEnquireDrawer/PromotionsEnquireDrawer'
import { AspectRatio } from '@/components/primitives/AspectRatio/AspectRatio'
import { Button, ButtonLink } from '@/components/primitives/Button/Button'
import { Heading } from '@/components/primitives/Heading/Heading'
import { cn, getImageUrl } from '@/lib/utils'

import RichTextEditor from '../RichTextEditor/RichTextEditor'

import type { PromoBannerType } from '@/types/contentful/promoBannerType'

type PromoBannerProps = PromoBannerType & { isDraftModeEnabled?: boolean }

export const PromoBanner = (fields: PromoBannerProps) => {
  if (!fields) return null
  const { actionButton, content, title, image, enquireFormLink } = fields
  const imageUrl = image?.fields?.file?.url && getImageUrl(image)

  return (
    <section data-name="promo-banner" className={cn('w-full bg-grey-100 px-4 py-8 md:px-12 md:py-16 lg:px-28 lg:py-24 xl:px-30')}>
      <div className="flex flex-col gap-6 rounded-xl bg-white p-6 shadow-sm md:flex-row md:gap-[92px] md:p-12 desktop:mx-auto desktop:max-w-content">
        <div className="flex flex-col gap-6 md:w-[40%]">
          {title && <Heading as="h2">{title}</Heading>}
          {content && (
            <div className="text-grey-700">
              <RichTextEditor content={content} />
            </div>
          )}
          {actionButton?.fields?.label && enquireFormLink && (
            <PromotionsEnquireDrawer formSrc={enquireFormLink}>
              <Button className="w-full md:w-fit">{actionButton?.fields?.label}</Button>
            </PromotionsEnquireDrawer>
          )}
          {!enquireFormLink && actionButton?.fields?.label && actionButton?.fields?.url && (
            <ButtonLink href={actionButton?.fields?.url} className="w-full md:w-fit">
              {actionButton?.fields?.label}
            </ButtonLink>
          )}
          <p className="text-grey-700">*Terms and conditions apply.</p>
        </div>
        <div className="w-full md:w-[60%] ">
          <AspectRatio ratios={{ mobile: '4/3', desktop: '4/3' }}>
            {image && imageUrl && (
              <Image
                src={imageUrl}
                alt={image.fields?.title ?? 'Promo Banner Image'}
                width={image?.fields?.file?.details?.image?.width ?? 430}
                height={image.fields.file.details.image?.height ?? 636}
                sizes="(max-width: 768px) 100vw, 60vw"
                className="size-full rounded-2xl object-contain"
                priority={true}
              />
            )}
          </AspectRatio>
        </div>
      </div>
    </section>
  )
}
